import { hexToUint8Array, uint8ArrayToHex } from "uint8array-extras";

export const MCP_CSRF_COOKIE_NAME = "mcp-csrf-token";
export const MCP_CSRF_HEADER_NAME = "x-csrf-token";

const MCP_CSRF_SECRET = process.env.MCP_CSRF_SECRET;
const EXEMPT_PATH_PREFIXES = [
  "/api/auth/",
  "/api/billing/set-limit",
  "/api/oauth/",
  "/api/v1/",
  "/api/v2/",
  "/api/zapier/",
  "/api/mcp/a/",
  "/api/mcp/mcp",
  "/api/mcp/sse",
  "/api/mcp/message",
];

// Known MCP transport names accepted by the backend (see https://gitlab.com/zapier/team-agents-platform/mcp/-/blob/main/apps/api/src/schemas/components/transport.ts#L20)
//
// This is used to distinguish protocol requests from browser REST calls under:
// - /api/mcp/s/
// - /api/mcp/embed/
//
// Everything else (actions, history, share, etc) need to keep CSRF protection.
// As written, newly added REST sub-resources are protected by default
const MCP_TRANSPORT_NAMES = new Set([
  "mcp",
  "sse",
  "http",
  "streamable-http",
  "message",
]);

const IGNORE_HTTP_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

// Matches /api/mcp/{s|embed}/{id} and optionally /{transport}
const MCP_PROTOCOL_RE = /^\/api\/mcp\/(?:s|embed)\/[^/]+(?:\/([^/]+))?$/;

// Memoize signing key to avoid creating a new instance for each request
let cachedKey: CryptoKey | null = null;

function buildSignedMessage(message: string, nonce: string): string {
  return `${message.length}!${message}!${nonce.length}!${nonce}`;
}

function generateNonce(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return uint8ArrayToHex(bytes);
}

async function getSigningKey(): Promise<CryptoKey> {
  if (cachedKey) {
    return cachedKey;
  }

  if (!MCP_CSRF_SECRET) {
    throw new Error("MCP_CSRF_SECRET environment variable is required");
  }

  cachedKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(MCP_CSRF_SECRET),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
  return cachedKey;
}

async function signMessage(message: string): Promise<string> {
  const key = await getSigningKey();
  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    new TextEncoder().encode(message),
  );

  return uint8ArrayToHex(new Uint8Array(signature));
}

async function verifySignedMessage(
  signatureHex: string,
  message: string,
): Promise<boolean> {
  const key = await getSigningKey();
  const signature = hexToUint8Array(signatureHex);

  return crypto.subtle.verify(
    "HMAC",
    key,
    signature,
    new TextEncoder().encode(message),
  );
}

export async function createCsrfToken(bindingValue: string): Promise<string> {
  const nonce = generateNonce();
  const message = buildSignedMessage(bindingValue, nonce);
  const hmac = await signMessage(message);
  return `${hmac}.${nonce}`;
}

function isMcpProtocolTransport(method: string, pathname: string): boolean {
  const match = pathname.match(MCP_PROTOCOL_RE);
  if (!match) {
    return false;
  }

  const transport = match[1];
  if (!transport) {
    return method === "POST";
  }

  return MCP_TRANSPORT_NAMES.has(transport);
}

export function requiresCsrfValidation(
  method: string,
  pathname: string,
): boolean {
  if (IGNORE_HTTP_METHODS.has(method)) {
    return false;
  }

  if (EXEMPT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return false;
  }

  if (isMcpProtocolTransport(method, pathname)) {
    return false;
  }

  return true;
}

export async function validateCsrfToken(
  tokenValue: string | undefined,
  bindingValue: string | undefined,
): Promise<boolean> {
  if (!tokenValue || !bindingValue) {
    return false;
  }

  const dotIndex = tokenValue.indexOf(".");
  if (dotIndex === -1) {
    return false;
  }

  const hmac = tokenValue.slice(0, dotIndex);
  const nonce = tokenValue.slice(dotIndex + 1);
  if (!hmac || !nonce) {
    return false;
  }

  const expectedMessage = buildSignedMessage(bindingValue, nonce);
  try {
    return await verifySignedMessage(hmac, expectedMessage);
  } catch (err) {
    console.warn("[CSRF] token verification error", err);
    return false;
  }
}
